"use client";

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { dismissWelcome } from "@/app/actions/welcome";
import { useLanguageStore } from "@/stores/language";
import { t } from "@/lib/i18n";
import { CREDITS_PER_VIDEO, SIGNUP_BONUS_GRANT, SIGNUP_BONUS_TTL_DAYS } from "@/lib/credits-config";

const AUTO_DISMISS_MS = 6000;
const MOUNT_DELAY_MS = 50;
const COLLAPSE_MS = 300;

export function WelcomeToast() {
  const { language } = useLanguageStore();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dismissWelcome().catch(() => {});
    setVisible(true);
    const showTimer = setTimeout(() => setOpen(true), MOUNT_DELAY_MS);
    const hideTimer = setTimeout(() => close(), AUTO_DISMISS_MS);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);

  function close() {
    setOpen(false);
    setTimeout(() => setVisible(false), COLLAPSE_MS);
  }

  if (!visible) return null;

  const videosCount = Math.floor(SIGNUP_BONUS_GRANT / CREDITS_PER_VIDEO);
  const message =
    t("welcome.body", language, { grant: SIGNUP_BONUS_GRANT, videos: videosCount, days: SIGNUP_BONUS_TTL_DAYS });

  return (
    <div
      className="grid"
      style={{
        gridTemplateRows: open ? "1fr" : "0fr",
        opacity: open ? 1 : 0,
        transition: `grid-template-rows ${COLLAPSE_MS}ms ease, opacity ${COLLAPSE_MS}ms ease`,
      }}
    >
      <div className="overflow-hidden">
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-3 rounded-2xl border border-primary/30 bg-primary px-4 py-3 text-primary-foreground shadow-sm"
        >
          <span className="text-xl leading-none" aria-hidden>🎉</span>
          <p className="flex-1 text-sm leading-snug">{message}</p>
          <button
            type="button"
            onClick={close}
            aria-label={t("common.dismiss", language)}
            className="-mr-1 -mt-0.5 rounded-full p-1 text-primary-foreground/80 transition hover:bg-white/10 hover:text-primary-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
